import { normalizeObjectTypeKey } from './pcfEtalonCatalog';

export type PcfObjectTypeCategoryKind =
  | 'unknown'
  | 'pipe'
  | 'flexPipe'
  | 'pipeFitting'
  | 'pipeAccessory'
  | 'mechanicalEquipment'
  | 'genericModel';

const PIPE_OBJECT_TYPES = [
  'IGNORE',
  'PIPE',
];

const PIPE_FITTING_OBJECT_TYPES = [
  'IGNORE',
  'BEND',
  'CAP',
  'COUPLING',
  'CROSS',
  'ELBOW',
  'FIELD-WELD',
  'FLANGE-BLIND',
  'GASKET',
  'MISC-COMPONENT',
  'OLET',
  'PLUG',
  'REDUCER-CONCENTRIC',
  'REDUCER-ECCENTRIC',
  'REINFORCEMENT-PAD',
  'TAP-CONNECTION',
  'TEE',
  'WELD',
];

const PIPE_ACCESSORY_OBJECT_TYPES = [
  'IGNORE',
  'BIP-IDENTIFIER',
  'BOLT',
  'COUPLING',
  'END-CONNECTION-EQUIPMENT',
  'FIELD-WELD',
  'FILTER',
  'FLANGE',
  'FLANGE-BLIND',
  'FLOOR-SYMBOL',
  'FLOW-ARROW',
  'GASKET',
  'INSTRUMENT',
  'ISO-SPLIT-POINT',
  'LAPJOINT-STUBEND',
  'LOCATION-POINT',
  'MESSAGE',
  'MESSAGE-CIRCLE',
  'MESSAGE-DIAMOND',
  'MESSAGE-DOUBLE-CIRCLE',
  'MESSAGE-ELLIPSE',
  'MESSAGE-ROUND',
  'MESSAGE-SQUARE',
  'MESSAGE-TRIANGLE',
  'MISC-COMPONENT',
  'ORIFICE',
  'PLUG',
  'REFERENCE-DIMENSION',
  'REFERENCE-POINT',
  'START-CO-ORDS',
  'SUPPORT',
  'UNION',
  'VALVE',
  'VALVE-3WAY',
  'VALVE-4WAY',
  'VALVE-ANGLE',
  'WELD',
];

const MECHANICAL_EQUIPMENT_OBJECT_TYPES = [
  'IGNORE',
  'END-CONNECTION-EQUIPMENT',
];

const GENERIC_MODEL_OBJECT_TYPES = [
  'IGNORE',
  'BIP-IDENTIFIER',
  'END-CONNECTION-EQUIPMENT',
  'FLOOR-SYMBOL',
  'FLOW-ARROW',
  'ISO-SPLIT-POINT',
  'LOCATION-POINT',
  'MESSAGE',
  'MESSAGE-CIRCLE',
  'MESSAGE-DIAMOND',
  'MESSAGE-DOUBLE-CIRCLE',
  'MESSAGE-ELLIPSE',
  'MESSAGE-ROUND',
  'MESSAGE-SQUARE',
  'MESSAGE-TRIANGLE',
  'REINFORCEMENT-PAD',
  'REFERENCE-DIMENSION',
  'REFERENCE-POINT',
  'START-CO-ORDS',
  'SUPPORT',
];

const COMPATIBLE_OBJECT_TYPES_BY_CATEGORY: Record<PcfObjectTypeCategoryKind, string[]> = {
  unknown: [],
  pipe: PIPE_OBJECT_TYPES,
  flexPipe: PIPE_OBJECT_TYPES,
  pipeFitting: PIPE_FITTING_OBJECT_TYPES,
  pipeAccessory: PIPE_ACCESSORY_OBJECT_TYPES,
  mechanicalEquipment: MECHANICAL_EQUIPMENT_OBJECT_TYPES,
  genericModel: GENERIC_MODEL_OBJECT_TYPES,
};

function normalizeCategoryName(categoryName?: string | null): string {
  return (categoryName ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');
}

function normalizeObjectTypeValues(values: string[]): string[] {
  return Array.from(
    new Set(
      values
        .map((value) => normalizeObjectTypeKey(value))
        .filter((value) => value.trim() !== '')
    )
  ).sort((left, right) => left.localeCompare(right));
}

export function resolvePcfObjectTypeCategoryKind(categoryName?: string | null): PcfObjectTypeCategoryKind {
  switch (normalizeCategoryName(categoryName)) {
    case 'pipes':
    case 'pipe':
    case 'pipecurves':
    case 'ostpipecurves':
      return 'pipe';
    case 'flexpipes':
    case 'flexpipe':
    case 'flexpipecurves':
    case 'ostflexpipecurves':
      return 'flexPipe';
    case 'pipefittings':
    case 'pipefitting':
    case 'ostpipefitting':
      return 'pipeFitting';
    case 'pipeaccessories':
    case 'pipeaccessory':
    case 'ostpipeaccessory':
      return 'pipeAccessory';
    case 'mechanicalequipment':
    case 'ostmechanicalequipment':
      return 'mechanicalEquipment';
    case 'genericmodels':
    case 'genericmodel':
    case 'ostgenericmodel':
      return 'genericModel';
    default:
      return 'unknown';
  }
}

export function getCompatibleObjectTypeValuesForCategory(categoryName?: string | null): string[] | null {
  const categoryKind = resolvePcfObjectTypeCategoryKind(categoryName);
  if (categoryKind === 'unknown') {
    return null;
  }

  return normalizeObjectTypeValues(COMPATIBLE_OBJECT_TYPES_BY_CATEGORY[categoryKind]);
}

export function getCommonCompatibleObjectTypeValuesForCategories(
  categories: Array<string | undefined | null>
): string[] | null {
  const categoryNames = categories
    .map((category) => (category ?? '').trim())
    .filter((category) => category !== '');

  if (categoryNames.length === 0) {
    return null;
  }

  const compatibleValues = categoryNames.map(getCompatibleObjectTypeValuesForCategory);
  if (compatibleValues.some((values) => values === null)) {
    return null;
  }

  const [firstValues = [], ...remainingValues] = compatibleValues as string[][];
  return firstValues.filter((value) =>
    remainingValues.every((values) => values.includes(value))
  );
}

export function parseObjectTypeValues(value?: string | null): string[] {
  if (!value) {
    return [];
  }

  return normalizeObjectTypeValues(value.split(/[,;|]/));
}

export function filterObjectTypeOptionsByValues<T extends { value: string }>(
  options: T[],
  compatibleValues: string[]
): T[] {
  const compatibleValueSet = new Set(compatibleValues.map((value) => normalizeObjectTypeKey(value)));
  return options.filter((option) => compatibleValueSet.has(normalizeObjectTypeKey(option.value)));
}

export function filterObjectTypeOptionsForCategory<T extends { value: string }>(
  options: T[],
  categoryName?: string | null
): T[] {
  const compatibleValues = getCompatibleObjectTypeValuesForCategory(categoryName);
  return compatibleValues === null ? options : filterObjectTypeOptionsByValues(options, compatibleValues);
}

export function filterObjectTypeOptionsForCategories<T extends { value: string }>(
  options: T[],
  categories: Array<string | undefined | null>
): T[] {
  const compatibleValues = getCommonCompatibleObjectTypeValuesForCategories(categories);
  return compatibleValues === null ? options : filterObjectTypeOptionsByValues(options, compatibleValues);
}
